import React, { useState } from 'react';
import { TemplateGroupHeading } from './TemplateSuggestionRow';

/**
 * A variant sub-pill's label — mirrors the medicine box's pre-existing
 * "★ Your pick" formatting exactly (dosage/duration/timing when present,
 * else form/strength). Pure, one place, so the medicine picker's ONE
 * derivation of a memory row never re-derives this per caller.
 */
function variantPillLabel(v) {
  return [v.dosage, v.duration, v.timing].filter(Boolean).join(' · ')
    || [v.form, v.strength].filter(Boolean).join(' ');
}

/**
 * MemorySuggestionRow — THE one row renderer for a doctor's own remembered
 * value, reused (never copy-pasted) by every field's dropdown: Diagnosis
 * (FE-3), Chief Complaint + Medicines (FE-3B), Tests lab ON/OFF (FE-4),
 * Advised surgeries (FE-6). `startsGroup`/`groupHeading`/`countLine` on the
 * row carry the FE-1/FE-3 group-heading + count-line contract; a caller
 * that has none of those on a given row just renders the plain button.
 *
 * `badge`/`subtitle`/`variants`/`onPickVariant` (FE-10) are Medicines-only —
 * every other field's caller leaves them `undefined` and the row renders
 * exactly as before. They exist here (not duplicated in a second Medicines-
 * only row component) so the ★ Your pick medicine row and the plain
 * "Your diagnoses" row share the SAME component per this epic's
 * single-source rule — FE-10 folded the medicine box's second ("doctor_history")
 * derivation into this ONE row, it never grew a sibling.
 *
 * `onForget`/`forgetAriaLabel`/`finePointer` (FE-12) — the "×" that lives at
 * the row's right end. Optional: a caller that omits `onForget` (Symptoms'
 * own chip-picker doesn't reuse this component at all, see
 * `DigitalPrescriptionForm.jsx`'s `symptomOption`) renders no forget chrome.
 * `onClick` stays on its OWN `<button>` (never nested inside the forget
 * button, or vice versa — nested buttons are invalid HTML and break
 * accessibility), so the two affordances sit as flex SIBLINGS inside one
 * non-button wrapper.
 *
 * Pulled out of `DigitalPrescriptionForm.jsx` into its own file (FE-4) so
 * `LabListPicker.jsx` can reuse it too without a circular import
 * (DigitalPrescriptionForm -> LabPanel -> LabListPicker).
 *
 * `type`/`ink`/`inkFaint` are plain style values (font-size strings / colour
 * strings) — a caller styled with Tailwind classes (LabListPicker) can pass
 * its own equivalents; this component only consumes the raw values.
 * `rowRef`/`onMouseMove` are optional — the Medicine box tracks the
 * keyboard-highlighted row for `scrollIntoView` the same way its own master/
 * template rows do; every other caller uses plain hover instead.
 *
 * `icon` (FIX-5, owner) — Medicines-only: the SAME 💊 glyph the org-list
 * medicine rows render (`MEDICINE_ROW_ICON` in `DigitalPrescriptionForm.jsx`,
 * one shared source, never a copy-pasted literal), at the org row's own
 * size/left position, so a "★ Your pick" row reads as the same kind of row.
 * Every other field's caller leaves it `undefined` and renders no icon,
 * exactly as before.
 *
 * `chip` (co-memory-backfill, C/O only today) — optional `{label, title}`;
 * reuses the SAME right-aligned slot `badge` already occupies, a sibling
 * span, never a parallel component. Marks a whole-LINE C/O row ("Typed" —
 * `CO_CHIP_LABEL`/`CO_CHIP_TITLE`, `rxMemory.js`); an individual-value row
 * passes `chip={null}` — absence IS the signal (stories.md Ratified copy
 * C3), so there is no visible marker on the other shape.
 */
export default function MemorySuggestionRow({
  label, groupHeading, countLine, startsGroup, highlighted, isDark, onClick,
  type, ink, inkFaint, rowRef, onMouseMove,
  badge, subtitle, variants, onPickVariant, icon, chip,
  onForget, forgetAriaLabel, finePointer,
}) {
  // FE-12 — hover-revealed on a fine pointer, always visible on touch
  // (`!finePointer`); Tab/focus into EITHER button in the row also reveals
  // it, so a keyboard user can actually see the control before activating
  // it (never opacity:0 while focused — that would make the "×" reachable
  // but invisible, which is worse than not offering it at all).
  const [chromeVisible, setChromeVisible] = useState(false);
  const forgetVisible = !finePointer || chromeVisible;

  return (
    <div>
      {startsGroup && (
        <>
          <TemplateGroupHeading label={groupHeading} isDark={isDark} />
          <div style={{
            padding: '0 0.625rem 0.35rem', fontSize: type.hint, color: inkFaint,
            fontFamily: 'system-ui, sans-serif',
          }}
          >{countLine}</div>
        </>
      )}
      <div
        style={{ display: 'flex', alignItems: 'stretch', width: '100%' }}
        onMouseEnter={() => setChromeVisible(true)}
        onMouseLeave={() => setChromeVisible(false)}
        onFocus={() => setChromeVisible(true)}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setChromeVisible(false); }}
      >
        <button data-source="doctor_memory" onClick={onClick} ref={rowRef} onMouseMove={onMouseMove}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem', flex: 1, minWidth: 0,
            padding: '0.4rem 0.625rem', border: 'none',
            background: highlighted
              ? (isDark ? 'rgba(13,148,136,0.16)' : '#f0fdfa')
              : 'transparent',
            cursor: 'pointer', fontSize: type.row,
            color: ink, textAlign: 'left', fontFamily: 'system-ui, sans-serif',
          }}
          onMouseEnter={onMouseMove ? undefined : (e) => { e.target.style.background = isDark ? 'rgba(13,148,136,0.1)' : 'rgba(13,148,136,0.04)'; }}
          onMouseLeave={onMouseMove ? undefined : (e) => { e.target.style.background = 'transparent'; }}
        >
          {/* FIX-5 — bare text (not a `<span>`): it inherits the button's
              own `fontSize: type.row`, the SAME size the org-list row's icon
              renders at, and sits first in the flex row (same left
              position) without becoming a second `<span>` ahead of the
              label — every existing "first `<span>` is the label" reader
              (medicine memory-row tests) keeps working unchanged. Absent
              (no icon) for every field but Medicines. */}
          {icon}
          {/* `minWidth: 0` + `overflow: hidden` lets the row's flex layout
              truncate a long label instead of wrapping (FE-12 — the "×"
              must never be pushed onto a second line). */}
          <span style={{
            flex: 1, minWidth: 0, fontWeight: badge ? 600 : 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
          >
            {label}
            {subtitle && (
              <span style={{ fontSize: type.rowSub || type.hint, color: inkFaint, marginLeft: '0.25rem', fontWeight: 400 }}>
                {subtitle}
              </span>
            )}
          </span>
          {badge && (
            <span style={{ flexShrink: 0, fontSize: type.badge, color: isDark ? '#818cf8' : '#6366f1', fontWeight: 600 }}>{badge}</span>
          )}
          {chip && (
            <span
              title={chip.title}
              aria-label={chip.title}
              style={{
                flexShrink: 0, fontSize: type.hint, fontWeight: 500, lineHeight: 1.3,
                padding: '0.05rem 0.375rem', borderRadius: '0.625rem',
                // CLAUDE.md chip rule — solid day tint + dark tint at >= /40
                // alpha; day ink is slate-600 (6.9:1 on #f1f5f9), slate-500
                // sat at 4.0:1, under AA for text this small.
                background: isDark ? 'rgba(100,116,139,0.40)' : '#f1f5f9',
                color: isDark ? '#cbd5e1' : '#475569',
              }}
            >{chip.label}</span>
          )}
        </button>
        {onForget && (
          <button
            type="button"
            aria-label={forgetAriaLabel}
            onClick={(e) => { e.stopPropagation(); onForget(); }}
            style={{
              flexShrink: 0, alignSelf: 'center', margin: '0 0.5rem 0 0.125rem',
              width: '1.25rem', height: '1.25rem', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
              borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: isDark ? 'rgba(239,68,68,0.18)' : '#fee2e2',
              color: '#ef4444', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1,
              opacity: forgetVisible ? 1 : 0,
            }}
          >✕</button>
        )}
      </div>
      {/* Variant sub-pills (FE-10) — same row identity, up to 3, ranked
          `count` desc by FE-1. Never a separate lookup: `variants` rides the
          SAME memory row this button renders. */}
      {variants && variants.length > 1 && (
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', padding: '0.1rem 0.625rem 0.4rem 1rem' }}>
          {variants.slice(0, 3).map((v, vi) => (
            <button key={vi} onClick={(e) => { e.stopPropagation(); onPickVariant(v); }}
              style={{
                padding: '0.15rem 0.5rem', borderRadius: '0.75rem', fontSize: type.chip, fontWeight: 500,
                border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.2)'}`,
                background: isDark ? 'rgba(99,102,241,0.14)' : '#eef2ff',
                color: isDark ? '#a5b4fc' : '#4f46e5', cursor: 'pointer', fontFamily: 'system-ui, sans-serif',
              }}
            >
              {variantPillLabel(v)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
